import { useState } from "react";
import { useParams, useLocation } from "wouter";
import {
  useGetSupportTicket,
  useUpdateTicketStatus,
  useAddTicketMessage,
  useGetMe,
  SupportTicketStatus,
} from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { formatDateTime } from "@/lib/format";
import { ArrowLeft, Paperclip, Send, User, ShieldCheck } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";

const BASE_URL = import.meta.env.BASE_URL.replace(/\/$/, "");

const typeLabels: Record<string, string> = {
  bug: "Bug Report",
  feature_request: "Feature Request",
  other: "Other",
};

const priorityColors: Record<string, string> = {
  low: "bg-slate-100 text-slate-700",
  medium: "bg-blue-100 text-blue-700",
  high: "bg-orange-100 text-orange-700",
  urgent: "bg-red-100 text-red-700",
};

const priorityLabels: Record<string, string> = {
  low: "Low",
  medium: "Medium",
  high: "High",
  urgent: "Urgent",
};

const statusColors: Record<string, string> = {
  open: "bg-green-100 text-green-700",
  in_progress: "bg-blue-100 text-blue-700",
  resolved: "bg-slate-100 text-slate-600",
  closed: "bg-slate-100 text-slate-500",
};

const statusLabels: Record<string, string> = {
  open: "Open",
  in_progress: "In Progress",
  resolved: "Resolved",
  closed: "Closed",
};

export default function TicketDetail() {
  const { id } = useParams<{ id: string }>();
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();

  const { data: me } = useGetMe();
  const { data: ticket, isLoading } = useGetSupportTicket(id!);
  const updateStatus = useUpdateTicketStatus();
  const addMessage = useAddTicketMessage();

  const [replyBody, setReplyBody] = useState("");
  const [isInternal, setIsInternal] = useState(false);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full rounded-xl" />
        <Skeleton className="h-32 w-full rounded-xl" />
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Ticket not found.</p>
        <Button variant="link" onClick={() => navigate("/support")}>
          Back to Support
        </Button>
      </div>
    );
  }

  const handleStatusChange = (status: string) => {
    const validStatus = Object.values(SupportTicketStatus).find((v) => v === status);
    if (!validStatus) return;
    updateStatus.mutate(
      { id: id!, data: { status: validStatus } },
      {
        onSuccess: () => {
          toast.success("Status updated");
          queryClient.invalidateQueries({ queryKey: ["getSupportTicket", id] });
        },
        onError: () => {
          toast.error("Failed to update status");
        },
      },
    );
  };

  const handleReply = () => {
    if (!replyBody.trim()) return;
    addMessage.mutate(
      { id: id!, data: { body: replyBody.trim(), isInternal } },
      {
        onSuccess: () => {
          toast.success(isInternal ? "Internal note added" : "Reply sent");
          setReplyBody("");
          setIsInternal(false);
          queryClient.invalidateQueries({ queryKey: ["getSupportTicket", id] });
        },
        onError: () => {
          toast.error("Failed to send reply");
        },
      },
    );
  };

  const attachmentUrl = ticket.attachmentPath
    ? `${BASE_URL}/api/storage${ticket.attachmentPath}`
    : null;

  return (
    <div className="space-y-6 animate-in fade-in duration-500 max-w-3xl">
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5 -ml-2"
          onClick={() => navigate(me?.isAdmin ? "/support/admin" : "/support")}
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
      </div>

      <Card>
        <CardHeader className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Badge
              variant="secondary"
              className={`text-xs ${priorityColors[ticket.priority] ?? ""}`}
            >
              {priorityLabels[ticket.priority]}
            </Badge>
            <Badge
              variant="secondary"
              className={`text-xs ${statusColors[ticket.status] ?? ""}`}
            >
              {statusLabels[ticket.status]}
            </Badge>
            <span className="text-sm text-muted-foreground">{typeLabels[ticket.type]}</span>
          </div>
          <CardTitle className="text-xl">{ticket.title}</CardTitle>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
            <span>
              <User className="inline h-3.5 w-3.5 mr-1" />
              {ticket.submitterName} ({ticket.submitterEmail})
            </span>
            <span>Submitted {formatDateTime(ticket.createdAt)}</span>
          </div>

          {me?.isAdmin && (
            <div className="pt-1">
              <Label className="text-xs text-muted-foreground mb-1.5 block">Change Status</Label>
              <Select value={ticket.status} onValueChange={handleStatusChange}>
                <SelectTrigger className="w-44">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-xs text-muted-foreground mb-1.5 block">Description</Label>
            <p className="text-sm whitespace-pre-wrap">{ticket.description}</p>
          </div>

          {attachmentUrl && ticket.attachmentName && (
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">Attachment</Label>
              <a
                href={attachmentUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
              >
                <Paperclip className="h-4 w-4" />
                {ticket.attachmentName}
              </a>
            </div>
          )}
        </CardContent>
      </Card>

      {ticket.messages && ticket.messages.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-base font-semibold">Conversation</h2>
          {ticket.messages.map((msg) => (
            <Card
              key={msg.id}
              className={msg.isInternal ? "border-yellow-300 bg-yellow-50/50" : ""}
            >
              <CardContent className="py-3 px-4">
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    {msg.isInternal && (
                      <span className="inline-flex items-center gap-1 text-xs text-yellow-700 font-normal">
                        <ShieldCheck className="h-3.5 w-3.5" />
                        Internal note
                      </span>
                    )}
                    <span>{msg.authorName}</span>
                    <span className="text-muted-foreground font-normal text-xs">
                      ({msg.authorEmail})
                    </span>
                  </div>
                  <span className="text-xs text-muted-foreground shrink-0">
                    {formatDateTime(msg.createdAt)}
                  </span>
                </div>
                <p className="text-sm whitespace-pre-wrap">{msg.body}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {me?.isAdmin ? "Reply or Add Note" : "Add a Reply"}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Textarea
            placeholder="Write your reply..."
            rows={4}
            value={replyBody}
            onChange={(e) => setReplyBody(e.target.value)}
            maxLength={5000}
          />
          <div className="flex items-center justify-between">
            {me?.isAdmin ? (
              <div className="flex items-center gap-2">
                <Checkbox
                  id="internal"
                  checked={isInternal}
                  onCheckedChange={(v) => setIsInternal(v === true)}
                />
                <Label htmlFor="internal" className="text-sm cursor-pointer">
                  Internal note (hidden from submitter)
                </Label>
              </div>
            ) : (
              <span />
            )}
            <Button
              onClick={handleReply}
              disabled={!replyBody.trim() || addMessage.isPending}
            >
              <Send className="mr-2 h-4 w-4" />
              {addMessage.isPending ? "Sending..." : "Send Reply"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
